/**
 * Auswahl im Monat (Board „Monat je Apartment“): ersten und letzten Tag anklicken oder ziehen;
 * Strg legt einen weiteren Zeitraum an, Umschalt verlängert den letzten – bis zu 50, angrenzende
 * verschmelzen. Baut auf derselben Auswahl wie der Grundkalender auf.
 */
import { useCallback, useRef } from 'react';
import { useRangeSelection } from '../calendar/useRangeSelection';
import type { SelectionMode } from '../calendar/rangeSelectionModel';

export function useMonthSelection(propertyId: string) {
  const selection = useRangeSelection();
  // Tag des letzten einfachen Klicks: der nächste einfache Klick setzt das Ende.
  const awaitingEnd = useRef<string | null>(null);

  const begin = useCallback(
    (date: string, mode: SelectionMode, immediate = false) => {
      const sameApartment = selection.propertyId === propertyId;
      const effective =
        mode === 'replace' && awaitingEnd.current && sameApartment ? 'extend' : mode;
      awaitingEnd.current = mode === 'replace' && effective === 'replace' ? date : null;
      selection.begin(propertyId, date, effective, immediate);
    },
    [propertyId, selection],
  );

  const extend = useCallback(
    (date: string) => {
      if (awaitingEnd.current && date !== awaitingEnd.current && selection.dragging) {
        awaitingEnd.current = null;
      }
      selection.extend(date);
    },
    [selection],
  );

  const clear = useCallback(() => {
    awaitingEnd.current = null;
    selection.clear();
  }, [selection]);

  // Chips ändern die Auswahl außerhalb des Rasters: der nächste Klick beginnt dann neu.
  const reset =
    <T extends unknown[]>(action: (...args: T) => void) =>
    (...args: T) => {
      awaitingEnd.current = null;
      action(...args);
    };
  return {
    ...selection,
    begin,
    extend,
    clear,
    addRange: reset(selection.addRange),
    patchRange: reset(selection.patchRange),
    removeRange: reset(selection.removeRange),
    select: reset(selection.select),
  };
}
